const BASE = '/api/v1';

// ---------------------------------------------------------------------------
// Outbox-eligible mutation allowlist (Decision 5 / Decision 6)
//
// Only these method+path families receive the Idempotency-Key and X-Outbox:
// queue headers. The list mirrors exactly the routes guarded by
// IdempotencyMiddleware in app/src/Http/Routes.php.
//
// shopping-add is keyed for replay safety: after merge-on-add, a non-keyed
// replay would double the quantity in the merged row (silent data corruption).
// The stable key makes the entire batch atomic under replay — it either runs
// once or returns the cached response.
//
// Put-away (/stock) is keyed for the same reason as the rest of this list:
// /stock creates an inventory item plus a shopping_item_stock_links audit
// row on every call, so a service-worker replay/retry of the SAME queued
// request (offline write queue, see below) would leave two inventory rows and
// two links behind. The key does NOT protect against an unkeyed double-tap —
// api() mints a fresh key per call (newOutboxKey() below), so two distinct
// taps still produce two distinct requests; StockShoppingItemHandler guards
// the sequential case server-side instead (already-stocked short-circuit —
// a read-before-transaction with no row lock, so it does not close the
// window for two genuinely concurrent calls).
//
// Timer create/control/cancel routes are keyed for the same reason: a
// replayed POST /timers must not create a second timer, and a replayed
// PATCH /timers/{id} (including the foreground elapse transition — see
// pages/timers.js) must not double-apply a control action. The key is
// generated once per logical call to api() (newOutboxKey()) and reused as
// the SW outbox entry id, so a network-failure retry or SW replay resends
// the SAME key rather than minting a new one per attempt.
// ---------------------------------------------------------------------------

import { newOutboxKey } from './outbox-core.js';

/** @type {Array<{method: string, pattern: RegExp}>} */
const OUTBOX_ALLOWLIST = [
  { method: 'POST',   pattern: /^\/inventory\/items$/ },
  { method: 'PATCH',  pattern: /^\/inventory\/items\/[^/]+$/ },
  { method: 'DELETE', pattern: /^\/inventory\/items\/[^/]+$/ },
  { method: 'POST',   pattern: /^\/inventory\/items\/[^/]+\/(consume|discard|move)$/ },
  { method: 'POST',   pattern: /^\/inventory\/quick-add$/ },
  { method: 'POST',   pattern: /^\/inventory\/quick-remove$/ },
  { method: 'POST',   pattern: /^\/shopping\/items$/ },
  { method: 'PATCH',  pattern: /^\/shopping\/items\/[^/]+$/ },
  { method: 'DELETE', pattern: /^\/shopping\/items\/[^/]+$/ },
  { method: 'POST',   pattern: /^\/shopping\/items\/[^/]+\/stock$/ },
  { method: 'POST',   pattern: /^\/recipes\/[^/]+\/shopping-add$/ },
  { method: 'POST',   pattern: /^\/timers$/ },
  { method: 'PATCH',  pattern: /^\/timers\/[^/]+$/ },
  { method: 'DELETE', pattern: /^\/timers\/[^/]+$/ },
];

/**
 * Returns true when the given method + path should be sent with outbox headers.
 *
 * @param {string} method  HTTP method (case-insensitive)
 * @param {string} path    Path under BASE (e.g. '/inventory/items')
 * @returns {boolean}
 */
function isOutboxEligible(method, path) {
  const upper = method.toUpperCase();
  return OUTBOX_ALLOWLIST.some((r) => r.method === upper && r.pattern.test(path));
}

export class ApiError extends Error {
  constructor(code, status, message, fields = null, body = null) {
    super(message);
    this.code = code;
    this.status = status;
    this.fields = fields;
    this.body = body;
  }
}

// Hook points so auth/router can clear on 401. Avoid circular imports
// by setting these from main.js at bootstrap.
let _onUnauthorized = () => {};
export function setUnauthorizedHandler(fn) { _onUnauthorized = fn; }

let _getToken = () => null;
export function setTokenProvider(fn) { _getToken = fn; }

// Backend uses this header to localize quick-add tile labels and the
// inventory `product_name` field. Wired via main.js at bootstrap so api.js
// stays free of i18n imports (and the circular dep i18n → api → i18n).
let _getLocale = () => null;
export function setLocaleProvider(fn) { _getLocale = fn; }

// Hook for showing toasts without importing toast.js directly (avoids
// pulling the DOM toast component into api.js and complicating tests).
let _showToast = () => {};
export function setToastProvider(fn) { _showToast = fn; }

// Hook for translating i18n keys without creating an import cycle
// (i18n.js imports api.js for apiGet).
let _t = (key) => key;
export function setTranslateProvider(fn) { _t = fn; }

// Module-level timer ID for the proactive refresh. Stored here so the
// canceler registered with auth.js can clear it regardless of when it is called.
let _proactiveTimerId = null;
let _cancelerRegistered = false;

// Hook so api.js can call auth.js's setRefreshTimerCanceler without creating
// a circular import (auth.js already imports api.js).
let _registerCanceler = () => {};
export function setTimerCancelerRegistrar(fn) { _registerCanceler = fn; }

// Callback invoked with the new token after a successful refresh, so callers
// that cache the token locally (auth.js, tests) can update their copy.
let _onRefreshSuccess = () => {};
export function setOnRefreshSuccess(fn) { _onRefreshSuccess = fn; }

// ---------------------------------------------------------------------------
// JWT helpers
// ---------------------------------------------------------------------------

function parseJwtExp(tok) {
  try {
    const part = tok.split('.')[1];
    const padded = part.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(padded));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Proactive refresh — schedule a timer to fire 5 min before JWT expiry
// ---------------------------------------------------------------------------

/**
 * Schedule a proactive refresh 5 minutes before the token's exp.
 * Cancellable via the hook registered with auth.js through setTimerCancelerRegistrar.
 * @param {string} tok  JWT to parse exp from
 */
export function scheduleProactiveRefresh(tok) {
  const exp = parseJwtExp(tok);
  if (exp === null) return;

  const msUntilExp = exp * 1000 - Date.now();
  const delay = msUntilExp - 300_000;

  // Already inside (or past) the 5-minute window — visibilitychange handles it
  if (delay <= 0) return;

  if (_proactiveTimerId !== null) clearTimeout(_proactiveTimerId);

  _proactiveTimerId = setTimeout(async () => {
    _proactiveTimerId = null;
    // If the tab is hidden, skip — visibilitychange will handle it on next focus
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    await _doRefresh();
  }, delay);

  // Register the canceler exactly once. The function closes over _proactiveTimerId
  // by reference so it always cancels whichever timer is currently pending.
  if (!_cancelerRegistered) {
    _cancelerRegistered = true;
    _registerCanceler(() => {
      if (_proactiveTimerId !== null) {
        clearTimeout(_proactiveTimerId);
        _proactiveTimerId = null;
      }
    });
  }
}

// ---------------------------------------------------------------------------
// visibilitychange listener — refresh when tab regains focus near expiry
// ---------------------------------------------------------------------------

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', async () => {
    if (document.visibilityState !== 'visible') return;
    const tok = _getToken();
    if (!tok) return;
    const exp = parseJwtExp(tok);
    if (exp === null) return;
    const msUntilExp = exp * 1000 - Date.now();
    if (msUntilExp < 300_000) {
      await _doRefresh();
    }
  });
}

// ---------------------------------------------------------------------------
// Single-flight refresh — collapse concurrent 401s into one /auth/refresh
// ---------------------------------------------------------------------------

/** @type {Promise<string>|null} */
let _inFlightRefresh = null;

/**
 * Perform a token refresh. Returns the new JWT on success.
 * Network errors and 5xx are classified as transient: toast shown, error
 * propagated; credentials are NOT cleared.
 * 401 from the refresh endpoint means the session is gone — propagate to
 * the caller (section 10 handles redirect + toast for that case).
 */
async function _doRefresh() {
  if (_inFlightRefresh) return _inFlightRefresh;

  _inFlightRefresh = (async () => {
    const tok = _getToken();
    const headers = { 'Content-Type': 'application/json' };
    if (tok) headers['Authorization'] = `Bearer ${tok}`;
    const loc = _getLocale();
    if (loc) headers['Accept-Language'] = loc;

    let res;
    try {
      res = await fetch(`${BASE}/auth/refresh`, { method: 'POST', headers });
    } catch {
      _showToast(_t('auth.toast.networkError'), 'error');
      throw new ApiError('network', 0, 'Network error');
    }

    if (res.status >= 500) {
      _showToast(_t('auth.toast.networkError'), 'error');
      throw new ApiError(`http_${res.status}`, res.status, 'Server error');
    }

    let data = null;
    const ct = res.headers.get('content-type') || '';
    if (ct.includes('application/json')) {
      try { data = await res.json(); } catch { data = null; }
    }

    if (res.status === 401) {
      const reason = (data && data.reason) || 'session_expired';
      throw new ApiError('refresh_401', 401, reason);
    }

    if (!res.ok) {
      _showToast(_t('auth.toast.networkError'), 'error');
      throw new ApiError(`http_${res.status}`, res.status, 'Refresh failed');
    }

    const newTok = data && data.token;
    if (!newTok) throw new ApiError('refresh_invalid', 0, 'No token in refresh response');

    _onRefreshSuccess(newTok);
    return newTok;
  })().finally(() => {
    _inFlightRefresh = null;
  });

  return _inFlightRefresh;
}

// ---------------------------------------------------------------------------
// Core fetch wrapper
// ---------------------------------------------------------------------------

export async function api(method, path, body) {
  const headers = { 'Content-Type': 'application/json' };
  const tok = _getToken();
  if (tok) headers['Authorization'] = `Bearer ${tok}`;
  const loc = _getLocale();
  if (loc) headers['Accept-Language'] = loc;

  // Outbox-eligible mutations carry a stable client-generated key (Decision 6).
  // The same key is sent as both the Idempotency-Key header (for server dedup)
  // and will become the outbox entry id in the SW (so replay reuses the key).
  if (isOutboxEligible(method, path)) {
    headers['Idempotency-Key'] = newOutboxKey();
    headers['X-Outbox'] = 'queue';
  }

  let res;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body == null ? undefined : JSON.stringify(body),
    });
  } catch (e) {
    throw new ApiError('network', 0, 'Network error');
  }

  // The SW returns a synthetic 202 {queued:true,key} when it captures a
  // failed network mutation into the outbox (Decision 5). Treat this as a
  // *pending* (not error) outcome so the page can render an optimistic row.
  if (res.status === 202) {
    let queuedData = null;
    const ct202 = res.headers.get('content-type') || '';
    if (ct202.includes('application/json')) {
      try { queuedData = await res.json(); } catch { queuedData = null; }
    }
    if (queuedData && queuedData.queued === true) {
      return queuedData;
    }
  }

  if (res.status === 401) {
    let newTok;
    try {
      newTok = await _doRefresh();
    } catch (refreshErr) {
      // Network/5xx toast was already shown inside _doRefresh; propagate
      // 401-from-refresh to the caller for section 10 to handle.
      if (refreshErr.status === 401) {
        _onUnauthorized(refreshErr.message);
      }
      throw refreshErr;
    }

    // Replay the original request with the fresh token
    const retryHeaders = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${newTok}` };
    if (loc) retryHeaders['Accept-Language'] = loc;

    let retryRes;
    try {
      retryRes = await fetch(`${BASE}${path}`, {
        method,
        headers: retryHeaders,
        body: body == null ? undefined : JSON.stringify(body),
      });
    } catch {
      throw new ApiError('network', 0, 'Network error');
    }

    if (retryRes.status === 401) {
      _onUnauthorized();
      throw new ApiError('unauthorized', 401, 'Unauthorized');
    }
    if (retryRes.status === 204) return null;

    let retryData = null;
    const retryCt = retryRes.headers.get('content-type') || '';
    if (retryCt.includes('application/json')) {
      try { retryData = await retryRes.json(); } catch { retryData = null; }
    }
    if (!retryRes.ok) {
      const code = (retryData && retryData.error && retryData.error.code) || `http_${retryRes.status}`;
      const msg = (retryData && retryData.error && retryData.error.message) || retryRes.statusText || 'Request failed';
      const fields = (retryData && retryData.fields) || null;
      throw new ApiError(code, retryRes.status, msg, fields, retryData);
    }
    return retryData;
  }

  if (res.status === 204) return null;

  let data = null;
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) {
    try { data = await res.json(); } catch { data = null; }
  }

  if (!res.ok) {
    const code = (data && data.error && data.error.code) || `http_${res.status}`;
    const msg = (data && data.error && data.error.message) || res.statusText || 'Request failed';
    const fields = (data && data.fields) || null;
    throw new ApiError(code, res.status, msg, fields, data);
  }
  return data;
}

// Convenience wrappers (used by pages)
export const apiGet   = (path) => api('GET', path);
export const apiPost  = (path, body) => api('POST', path, body);
export const apiPatch = (path, body) => api('PATCH', path, body);
export const apiPut   = (path, body) => api('PUT', path, body);
export const apiDel   = (path) => api('DELETE', path);

// ---------------------------------------------------------------------------
// Reminder-specific helpers
// ---------------------------------------------------------------------------

/**
 * Mark reminders as read for the current user.
 *
 * @param {{ ids?: string[], all?: boolean }} opts  Pass `ids` to mark specific
 *   reminders or `all: true` to mark all visible reminders read.
 * @returns {Promise<{ unread_count: number }>}
 */
export function markRemindersRead({ ids, all } = {}) {
  const body = all ? { all: true } : { ids: ids ?? [] };
  return api('POST', '/reminders/mark-read', body);
}

/**
 * Snooze a reminder for the current user.
 *
 * @param {string} id  Reminder ID
 * @param {{ duration?: '1h'|'1d', until?: string }} opts
 *   Pass `duration` for a preset snooze or `until` (ISO-8601) for a specific time.
 * @returns {Promise<{ snoozed_until: string, unread_count: number }>}
 */
export function snoozeReminder(id, { duration, until } = {}) {
  const body = until != null ? { until } : { duration };
  return api('POST', `/reminders/${encodeURIComponent(id)}/snooze`, body);
}

/**
 * Remove a snooze for the current user on the given reminder.
 *
 * @param {string} id  Reminder ID
 * @returns {Promise<null>}  204 No Content → null
 */
export function unsnoozeReminder(id) {
  return api('DELETE', `/reminders/${encodeURIComponent(id)}/snooze`);
}

/**
 * Dismiss (cancel) a reminder for the whole household. The reminder leaves
 * every member's centre; it reappears only if the item's expiry is re-edited.
 *
 * @param {string} id  Reminder ID
 * @returns {Promise<null>}  204 No Content → null
 */
export function dismissReminder(id) {
  return api('POST', `/reminders/${encodeURIComponent(id)}/dismiss`);
}

/**
 * Fetch an authenticated image and return a blob object URL.
 *
 * The `/api/v1/uploads/product-masters/…` serve endpoint requires a Bearer
 * token via AuthMiddleware, which `<img src>` tags cannot supply. Call this
 * helper to fetch the image with the JWT header and convert it to a
 * `blob:` URL that the browser can render without auth.
 *
 * Callers are responsible for revoking the blob URL via
 * `URL.revokeObjectURL(url)` when the image element is removed from the DOM
 * to avoid memory leaks.
 *
 * @param {string} path  path under BASE, e.g. '/uploads/product-masters/abc.jpg'
 * @returns {Promise<string>}  a `blob:` object URL
 */
export async function apiFetchImage(path) {
  const headers = {};
  const tok = _getToken();
  if (tok) headers['Authorization'] = `Bearer ${tok}`;
  const loc = _getLocale();
  if (loc) headers['Accept-Language'] = loc;

  let res;
  try {
    res = await fetch(`${BASE}${path}`, { headers });
  } catch {
    throw new ApiError('network', 0, 'Network error');
  }

  if (res.status === 401) {
    _onUnauthorized();
    throw new ApiError('unauthorized', 401, 'Unauthorized');
  }
  if (!res.ok) {
    throw new ApiError(`http_${res.status}`, res.status, 'Image fetch failed');
  }

  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/**
 * POST a multipart/form-data body (e.g. file uploads). The browser sets the
 * multipart Content-Type with the right boundary automatically when we omit
 * it from the headers, so this helper only adds Authorization / Accept-Language.
 *
 * Returns the parsed JSON body on success. Throws ApiError on non-2xx, mirroring
 * the shape used by `api()` so call sites handle errors the same way.
 *
 * @param {string} path  path under BASE, e.g. '/ocr/expiry'
 * @param {FormData} formData
 * @param {{ signal?: AbortSignal }} [opts]
 */
export async function apiPostMultipart(path, formData, opts = {}) {
  /** @type {Record<string,string>} */
  const headers = {};
  const tok = _getToken();
  if (tok) headers['Authorization'] = `Bearer ${tok}`;
  const loc = _getLocale();
  if (loc) headers['Accept-Language'] = loc;

  let res;
  try {
    res = await fetch(`${BASE}${path}`, {
      method: 'POST',
      headers,
      body: formData,
      signal: opts.signal,
    });
  } catch (e) {
    throw new ApiError('network', 0, 'Network error');
  }

  if (res.status === 401) {
    _onUnauthorized();
    throw new ApiError('unauthorized', 401, 'Unauthorized');
  }
  if (res.status === 204) return null;

  let data = null;
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) {
    try { data = await res.json(); } catch { data = null; }
  }

  if (!res.ok) {
    const code = (data && data.error && data.error.code) || `http_${res.status}`;
    const msg = (data && data.error && data.error.message) || res.statusText || 'Request failed';
    throw new ApiError(code, res.status, msg);
  }
  return data;
}
